import React, { useState, useEffect, useRef } from 'react';
import API, { BACKEND_URL } from '../api';
import { Plus, X, FolderOpen, User, FileText, HardHat, MessageCircle, Upload, Paperclip, Send, Bot, ArrowLeft, Sparkles, Clock, ChevronRight, Trash2, RefreshCw, File, Image, Download } from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';
import MultiClienteSelect from '../components/MultiClienteSelect';
import RowActions from '../components/RowActions';

const statiLabels = { nuova: 'Nuova', in_valutazione: 'In Valutazione', sopralluogo_fissato: 'Sopralluogo Fissato', sopralluogo_eseguito: 'Sopralluogo Eseguito', preventivo_in_bozza: 'Preventivo in Bozza', preventivo_inviato: 'Preventivo Inviato', approvata: 'Approvata', in_cantiere: 'In Cantiere', completata: 'Completata', rifiutata: 'Rifiutata' };
const statiColors = { nuova: 'bg-blue-100 text-blue-700', in_valutazione: 'bg-yellow-100 text-yellow-800', sopralluogo_fissato: 'bg-purple-100 text-purple-700', approvata: 'bg-green-100 text-green-700', in_cantiere: 'bg-emerald-100 text-emerald-800', completata: 'bg-slate-200 text-slate-600', rifiutata: 'bg-red-100 text-red-700' };

export default function PraticaHubPage({ onNavigate }) {
  const confirm = useConfirm();
  const [pratiche, setPratiche] = useState([]);
  const [selected, setSelected] = useState(null);
  const [fascicolo, setFascicolo] = useState(null);
  const [clienti, setClienti] = useState([]);
  const [tab, setTab] = useState('overview');
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ titolo: '', descrizione_richiesta: '', indirizzo_immobile: '', cliente_id: '', clienti_ids: [], note_interne: '' });
  const [showNewCliente, setShowNewCliente] = useState(false);
  const [clienteForm, setClienteForm] = useState({ tipo_cliente: 'privato', nome: '', cognome: '', ragione_sociale: '', telefono: '', email: '' });
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [riassuntoLoading, setRiassuntoLoading] = useState(false);
  const chatEndRef = useRef(null);
  const fileRef = useRef(null);

  const load = () => { API.get('/api/pratiche').then(r => setPratiche(r.data)); API.get('/api/clienti').then(r => setClienti(r.data)); };
  useEffect(() => { load(); }, []);

  const openPratica = async (p) => {
    setSelected(p);
    setTab('overview');
    const { data } = await API.get(`/api/pratiche/${p.id}/fascicolo`);
    setFascicolo(data);
    setChatMsgs(data.chat || []);
    setDocs(data.documenti || []);
  };

  const createPratica = async () => {
    const { data } = await API.post('/api/pratiche', form);
    setShowNew(false);
    load();
    openPratica(data);
  };

  const createCliente = async () => {
    try {
      const { data } = await API.post('/api/clienti', clienteForm);
      setShowNewCliente(false);
      setClienteForm({ tipo_cliente: 'privato', nome: '', cognome: '', ragione_sociale: '', telefono: '', email: '' });
      const res = await API.get('/api/clienti');
      setClienti(res.data);
      // Auto-select the newly created client (aggiunge alla lista clienti_ids)
      setForm(f => {
        const newIds = [...(f.clienti_ids || []), data.id].filter((v, i, arr) => arr.indexOf(v) === i);
        return { ...f, cliente_id: newIds[0] || data.id, clienti_ids: newIds };
      });
      alert(`Cliente "${data.ragione_sociale || data.nome + ' ' + data.cognome}" creato e selezionato!`);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  const updateStato = async (stato) => {
    await API.put(`/api/pratiche/${selected.id}`, { stato });
    const { data } = await API.get(`/api/pratiche/${selected.id}`);
    setSelected(data);
    setFascicolo(f => ({ ...f, pratica: data }));
    load();
  };

  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    setChatLoading(true);
    try {
      const { data } = await API.post(`/api/pratiche/${selected.id}/chat`, { messaggio: chatInput });
      setChatInput('');
      const chatRes = await API.get(`/api/pratiche/${selected.id}/chat`);
      setChatMsgs(chatRes.data);
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    }
    setChatLoading(false);
  };

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMsgs]);

  const uploadDoc = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('categoria', 'generico');
    try {
      await API.post(`/api/pratiche/${selected.id}/documenti`, fd);
      const { data } = await API.get(`/api/pratiche/${selected.id}/documenti`);
      setDocs(data);
    } catch (err) { console.error('Upload documento pratica fallito:', err); }
    setUploading(false);
  };

  const deleteDoc = async (did) => {
    if (await confirm({ title: 'Eliminare documento?', message: 'Il file verrà rimosso dalla pratica.', confirmLabel: 'Elimina', variant: 'danger' })) {
      await API.delete(`/api/pratiche/${selected.id}/documenti/${did}`);
      const { data } = await API.get(`/api/pratiche/${selected.id}/documenti`);
      setDocs(data);
    }
  };

  const aggiornaRiassunto = async () => {
    setRiassuntoLoading(true);
    try {
      const { data } = await API.post(`/api/pratiche/${selected.id}/aggiorna-riassunto`);
      setFascicolo(f => ({ ...f, pratica: { ...f.pratica, ai_riassunto: data.riassunto } }));
      setSelected(s => ({ ...s, ai_riassunto: data.riassunto }));
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    }
    setRiassuntoLoading(false);
  };

  const getClienteName = (id) => { const c = clienti.find(cl => cl.id === id); return c ? (c.ragione_sociale || `${c.nome} ${c.cognome}`) : '-'; };
  const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  // ── Detail View ───────────────────────────────────────────────────
  if (selected && fascicolo) {
    const p = fascicolo.pratica;
    const tabs = [
      { id: 'overview', label: 'Panoramica' },
      { id: 'chat', label: `Chat AI (${chatMsgs.length})` },
      { id: 'documenti', label: `Documenti (${docs.length})` },
    ];

    return (
      <div className="animate-fade-in" data-testid="pratica-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <button onClick={() => { setSelected(null); setFascicolo(null); }} className="p-2 hover:bg-slate-100 rounded-md flex-shrink-0" data-testid="back-pratiche">
              <ArrowLeft size={20} />
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight truncate">{p.titolo}</h1>
                <span className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded flex-shrink-0">{p.codice_pratica}</span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold flex-shrink-0 ${statiColors[p.stato] || 'bg-slate-100'}`}>{statiLabels[p.stato] || p.stato}</span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{p.descrizione_richiesta}</p>
            </div>
          </div>
          <select className="px-3 py-1.5 border border-slate-200 rounded-md text-sm flex-shrink-0 self-start sm:self-auto" value={p.stato} onChange={e => updateStato(e.target.value)}>
            {Object.entries(statiLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-white border border-slate-200 rounded-md p-1 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded text-sm font-bold transition-all ${tab === t.id ? 'bg-surface-dark text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="space-y-4">
            {/* Info + Client */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><FolderOpen size={16} className="text-primary" /> Dettagli Pratica</h3>
                <div className="space-y-2 text-sm">
                  <Row label="Codice" value={p.codice_pratica} />
                  <Row label="Indirizzo" value={p.indirizzo_immobile || '-'} />
                  {fascicolo.clienti && fascicolo.clienti.length > 0 ? (
                    <div className="flex items-start gap-2">
                      <span className="text-slate-500 font-semibold min-w-[80px]">Client{fascicolo.clienti.length > 1 ? 'i' : 'e'}:</span>
                      <div className="flex-1 flex flex-wrap gap-1">
                        {fascicolo.clienti.map((c, idx) => (
                          <span key={c.id} className={`px-2 py-0.5 rounded text-xs font-bold ${idx === 0 ? 'bg-primary text-primary' : 'bg-blue-50 text-blue-800'}`}>
                            {idx === 0 ? '★ ' : ''}{c.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Row label="Cliente" value={fascicolo.cliente ? (fascicolo.cliente.ragione_sociale || `${fascicolo.cliente.nome} ${fascicolo.cliente.cognome}`) : 'Non assegnato'} />
                  )}
                  <Row label="Creata" value={p.created_at ? new Date(p.created_at).toLocaleDateString('it-IT') : '-'} />
                  {p.note_interne && <Row label="Note" value={p.note_interne} />}
                </div>
              </div>
              {/* AI Riassunto */}
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm flex items-center gap-2"><Bot size={16} className="text-accent-info" /> Riassunto AI Cantiere</h3>
                  <button onClick={aggiornaRiassunto} disabled={riassuntoLoading} className="px-3 py-1 text-xs font-bold text-accent-info border border-accent-info/30 rounded-md hover:bg-blue-50 flex items-center gap-1 disabled:opacity-50">
                    <RefreshCw size={12} className={riassuntoLoading ? 'animate-spin' : ''} /> Aggiorna
                  </button>
                </div>
                <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{p.ai_riassunto || 'Nessun riassunto ancora. Clicca "Aggiorna" dopo che ci sono messaggi nel cantiere.'}</p>
              </div>
            </div>

            {/* Linked entities */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Computi */}
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm flex items-center gap-2"><FileText size={16} className="text-primary" /> Computi ({fascicolo.computi?.length || 0})</h3>
                  <button onClick={() => onNavigate('computi', { pratica_id: selected.id })} className="text-xs text-primary font-semibold hover:underline">+ Nuovo</button>
                </div>
                {fascicolo.computi?.map(c => (
                  <button key={c.id} onClick={() => onNavigate('computi', { computo_id: c.id, pratica_id: selected.id })} className="w-full text-left py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 rounded px-2 -mx-2 transition-colors" data-testid={`open-computo-${c.id}`}>
                    <p className="text-sm font-semibold">{c.titolo || c.codice_computo}</p>
                    <p className="text-xs text-slate-400">{c.codice_computo} - {c.righe_totali || '?'} righe</p>
                  </button>
                ))}
                {!fascicolo.computi?.length && <p className="text-xs text-slate-400">Nessun computo</p>}
              </div>

              {/* Preventivi */}
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm flex items-center gap-2"><FileText size={16} className="text-accent-info" /> Preventivi ({fascicolo.preventivi?.length || 0})</h3>
                  <button onClick={() => onNavigate('preventivi', { pratica_id: selected.id, pratica_titolo: selected.titolo })} className="text-xs text-primary font-semibold hover:underline">+ Nuovo</button>
                </div>
                {fascicolo.preventivi?.map(pr => (
                  <button key={pr.id} onClick={() => onNavigate('preventivi', { preventivo_id: pr.id, pratica_id: selected.id, pratica_titolo: selected.titolo })} className="w-full text-left py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 rounded px-2 -mx-2 transition-colors" data-testid={`open-preventivo-${pr.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">{pr.titolo || pr.numero_preventivo}</p>
                      <span className="font-mono text-xs font-bold">{fmt(pr.totale_finale)}</span>
                    </div>
                    <p className="text-xs text-slate-400">{pr.numero_preventivo} · {pr.stato}</p>
                  </button>
                ))}
                {!fascicolo.preventivi?.length && <p className="text-xs text-slate-400">Nessun preventivo</p>}
              </div>

              {/* Cantieri */}
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm flex items-center gap-2"><HardHat size={16} className="text-accent-success" /> Cantieri ({fascicolo.cantieri?.length || 0})</h3>
                </div>
                {fascicolo.cantieri?.map(ca => (
                  <div key={ca.id} className="py-2 border-b border-slate-100 last:border-0">
                    <button onClick={() => onNavigate('cantieri', { cantiere_id: ca.id })} className="w-full text-left hover:bg-slate-50 rounded transition-colors p-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold">{ca.titolo || ca.codice_cantiere}</p>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ca.stato === 'in_corso' ? 'bg-green-100 text-green-700' : ca.stato === 'completato' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100'}`}>{ca.stato}</span>
                      </div>
                      <p className="text-xs text-slate-400">{ca.codice_cantiere} - {fascicolo.messaggi_cantiere} msg</p>
                    </button>
                    <div className="flex gap-1.5 mt-1.5">
                      <button onClick={() => onNavigate('cantieri', { cantiere_id: ca.id, tab: 'sal' })}
                        className="text-[11px] px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-bold"
                        data-testid={`crea-sal-pratica-${ca.id}`}>
                        + SAL
                      </button>
                      <CreaFatturaFromPraticaBtn cantiere={ca} />
                    </div>
                  </div>
                ))}
                {!fascicolo.cantieri?.length && <p className="text-xs text-slate-400">Nessun cantiere (approva un preventivo per aprire)</p>}
              </div>
            </div>
          </div>
        )}

        {/* Chat AI Tab */}
        {tab === 'chat' && (
          <div className="flex flex-col bg-white border border-slate-200 rounded-md" style={{ height: 'calc(100vh - 300px)', minHeight: '400px' }}>
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <Bot size={18} className="text-primary" />
              <span className="font-bold text-sm">Assistente AI Pratica</span>
              <span className="text-xs text-slate-400">Le conversazioni sono storicizzate</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="pratica-chat">
              {chatMsgs.length === 0 && <p className="text-center text-slate-400 text-sm py-8">Nessun messaggio. Chiedi qualcosa sull'AI sulla pratica!</p>}
              {chatMsgs.map((m, i) => (
                <div key={i} className={`flex ${m.ruolo === 'utente' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-lg px-4 py-2.5 ${m.ruolo === 'utente' ? 'bg-slate-100' : 'bg-white border border-slate-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {m.ruolo === 'ai' && <Bot size={12} className="text-primary" />}
                      <span className="text-xs font-bold text-slate-500">{m.ruolo === 'ai' ? 'EDILCIMA AI' : m.utente_nome}</span>
                      <span className="text-[10px] text-slate-300">{m.created_at ? new Date(m.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{m.messaggio}</p>
                  </div>
                </div>
              ))}
              {chatLoading && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-500 flex items-center gap-2"><Bot size={14} className="animate-pulse text-primary" />L'AI sta rispondendo...</div></div>}
              <div ref={chatEndRef} />
            </div>
            <div className="p-3 border-t border-slate-200 flex gap-2">
              <input data-testid="pratica-chat-input" placeholder="Chiedi qualcosa sulla pratica..." className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-primary/50"
                value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()} disabled={chatLoading} />
              <button onClick={sendChat} disabled={chatLoading || !chatInput.trim()} data-testid="pratica-chat-send" className="p-2 bg-primary text-white rounded-md hover:bg-primary-hover disabled:opacity-50">
                <Send size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Documenti Tab */}
        {tab === 'documenti' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">Carica documenti di qualsiasi formato nella pratica</p>
              <div>
                <input type="file" ref={fileRef} className="hidden" onChange={uploadDoc} />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-doc-pratica"
                  className="px-4 py-2 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all flex items-center gap-2 disabled:opacity-50">
                  <Upload size={16} /> {uploading ? 'Caricamento...' : 'Carica Documento'}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {docs.map(d => (
                <div key={d.id} className="bg-white border border-slate-200 rounded-md p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-md flex items-center justify-center">
                      {d.tipo_file && ['jpg','jpeg','png','webp'].includes(d.tipo_file) ? <Image size={18} className="text-slate-400" /> : <File size={18} className="text-slate-400" />}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{d.filename}</p>
                      <p className="text-xs text-slate-400">{d.tipo_file?.toUpperCase()} - {d.dimensione ? `${(d.dimensione/1024).toFixed(1)} KB` : ''} - {d.created_at ? new Date(d.created_at).toLocaleDateString('it-IT') : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`${BACKEND_URL}${d.file_url}`} target="_blank" rel="noreferrer" className="p-2 hover:bg-slate-100 rounded-md"><Download size={16} className="text-slate-400" /></a>
                    <button onClick={() => deleteDoc(d.id)} className="p-2 hover:bg-red-50 rounded-md"><Trash2 size={16} className="text-red-400" /></button>
                  </div>
                </div>
              ))}
              {docs.length === 0 && <p className="text-center py-8 text-slate-400 text-sm">Nessun documento. Carica il primo!</p>}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── List View ─────────────────────────────────────────────────────
  return (
    <div className="animate-fade-in" data-testid="pratiche-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-3"><FolderOpen size={28} className="text-primary" /> Pratiche</h1>
          <p className="text-slate-500 mt-1">Hub centrale per ogni lavoro: dal preventivo al cantiere</p>
        </div>
        <button data-testid="add-pratica-btn" onClick={() => { setShowNew(true); setForm({ titolo: '', descrizione_richiesta: '', indirizzo_immobile: '', cliente_id: '', clienti_ids: [], note_interne: '' }); }}
          className="px-4 py-2 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95 flex items-center gap-2">
          <Plus size={18} /> Nuova Pratica
        </button>
      </div>

      <div className="space-y-3">
        {pratiche.map(p => (
          <div key={p.id} data-testid={`pratica-${p.id}`}
            className="w-full bg-white border border-slate-200 rounded-md p-4 text-left hover:shadow-sm transition-all group flex items-center gap-3">
            <div onClick={() => openPratica(p)} className="flex-1 cursor-pointer flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{p.codice_pratica || '???'}</span>
                  <h3 className="font-bold">{p.titolo}</h3>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${statiColors[p.stato] || 'bg-slate-100'}`}>{statiLabels[p.stato] || p.stato}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{p.descrizione_richiesta}</p>
                <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                  {(() => {
                    const ids = (p.clienti_ids && p.clienti_ids.length > 0) ? p.clienti_ids : (p.cliente_id ? [p.cliente_id] : []);
                    if (ids.length === 0) return null;
                    if (ids.length === 1) return <span>Cliente: {getClienteName(ids[0])}</span>;
                    return <span>Clienti: {getClienteName(ids[0])} <b className="text-primary">+{ids.length - 1}</b></span>;
                  })()}
                  {p.indirizzo_immobile && <span>{p.indirizzo_immobile}</span>}
                </div>
              </div>
              <ChevronRight size={20} className="text-slate-300 group-hover:text-primary transition-colors flex-shrink-0" />
            </div>
            <RowActions entity="pratiche" id={p.id} label={`la pratica "${p.titolo}"`} onChange={load} compact />
          </div>
        ))}
        {pratiche.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <FolderOpen size={48} className="mx-auto mb-3 text-slate-300" />
            <p className="font-semibold">Nessuna pratica</p>
            <p className="text-sm mt-1">Crea la prima pratica per iniziare a gestire un lavoro</p>
          </div>
        )}
      </div>

      {/* New Pratica Modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-4xl max-h-[92vh] sm:max-h-[90vh] flex flex-col animate-fade-in shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-200 bg-white sticky top-0 z-10">
              <h3 className="text-xl font-bold">Nuova Pratica</h3>
              <button onClick={() => setShowNew(false)} aria-label="Chiudi"><X size={20} className="text-slate-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 sm:p-6">
            <div className="space-y-3">
              <input data-testid="pratica-titolo" placeholder="Titolo pratica (es: Ristrutturazione Bagno Rossi)" className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-primary/50"
                value={form.titolo} onChange={e => setForm({ ...form, titolo: e.target.value })} />
              <textarea data-testid="pratica-descrizione" placeholder="Descrizione della richiesta" rows={3} className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-primary/50"
                value={form.descrizione_richiesta} onChange={e => setForm({ ...form, descrizione_richiesta: e.target.value })} />
              <input placeholder="Indirizzo immobile" className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={form.indirizzo_immobile} onChange={e => setForm({ ...form, indirizzo_immobile: e.target.value })} />
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Clienti / Pagatori <span className="text-slate-400 normal-case font-medium">— il primo è il principale</span></label>
                  <button type="button" onClick={() => setShowNewCliente(true)} data-testid="new-cliente-from-pratica"
                    className="px-2.5 py-1 bg-accent-info text-white font-bold rounded text-[11px] hover:bg-blue-700 flex items-center gap-1">
                    <Plus size={12} /> Nuovo cliente
                  </button>
                </div>
                <MultiClienteSelect
                  value={form.clienti_ids || []}
                  onChange={(ids) => setForm({ ...form, clienti_ids: ids, cliente_id: ids[0] || '' })}
                  clienti={clienti}
                  placeholder="Seleziona uno o più clienti (opzionale)"
                  testid="pratica-clienti"
                />
              </div>
              <textarea placeholder="Note interne" rows={2} className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={form.note_interne} onChange={e => setForm({ ...form, note_interne: e.target.value })} />
            </div>
            </div>
            <div className="p-4 sm:p-5 border-t border-slate-200 bg-white sticky bottom-0">
              <button data-testid="save-pratica-btn" onClick={createPratica} disabled={!form.titolo.trim()} className="w-full py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95 disabled:opacity-50">
                Crea Pratica
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Cliente Sub-Modal */}
      {showNewCliente && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[90vh] flex flex-col animate-fade-in shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-200 bg-white sticky top-0 z-10">
              <h3 className="text-lg font-bold">Nuovo Cliente</h3>
              <button onClick={() => setShowNewCliente(false)} aria-label="Chiudi"><X size={20} className="text-slate-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 sm:p-6">
            <div className="space-y-3">
              <select className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={clienteForm.tipo_cliente} onChange={e => setClienteForm({...clienteForm, tipo_cliente: e.target.value})}>
                <option value="privato">Privato</option>
                <option value="azienda">Azienda</option>
              </select>
              {clienteForm.tipo_cliente === 'azienda' ? (
                <input data-testid="new-cliente-ragione" placeholder="Ragione Sociale" className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={clienteForm.ragione_sociale} onChange={e => setClienteForm({...clienteForm, ragione_sociale: e.target.value})} />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="new-cliente-nome" placeholder="Nome" className="px-3 py-2.5 border border-slate-200 rounded-md" value={clienteForm.nome} onChange={e => setClienteForm({...clienteForm, nome: e.target.value})} />
                  <input data-testid="new-cliente-cognome" placeholder="Cognome" className="px-3 py-2.5 border border-slate-200 rounded-md" value={clienteForm.cognome} onChange={e => setClienteForm({...clienteForm, cognome: e.target.value})} />
                </div>
              )}
              <input placeholder="Telefono" className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={clienteForm.telefono} onChange={e => setClienteForm({...clienteForm, telefono: e.target.value})} />
              <input placeholder="Email" className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={clienteForm.email} onChange={e => setClienteForm({...clienteForm, email: e.target.value})} />
            </div>
            </div>
            <div className="p-4 sm:p-5 border-t border-slate-200 bg-white sticky bottom-0">
              <button data-testid="save-new-cliente-btn" onClick={createCliente} className="w-full py-2.5 bg-accent-info text-white font-bold rounded-md hover:bg-blue-700 transition-all active:scale-95">
                Crea Cliente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex">
      <span className="text-xs font-bold text-slate-400 w-24 flex-shrink-0">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

function CreaFatturaFromPraticaBtn({ cantiere }) {
  const [busy, setBusy] = useState(false);
  if (!cantiere) return null;
  const click = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Creare bozza fattura dal cantiere?\n\nVerranno inclusi: SAL approvati + lavorazioni extra completate. Potrai modificarla in Contabilità.')) return;
    setBusy(true);
    try {
      const { data } = await API.post(`/api/fatture/da-cantiere/${cantiere.id}`, { include_extra: true, tipo: 'fattura' });
      alert(`✓ Fattura ${data.numero_fattura} creata. Vai in Contabilità → Fatture.`);
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };
  return (
    <button onClick={click} disabled={busy}
      className="text-[11px] px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 font-bold disabled:opacity-50"
      data-testid={`crea-fattura-pratica-${cantiere.id}`}>
      {busy ? '…' : '€ Fattura'}
    </button>
  );
}
